import React from "react";
import {
  Image,
  Platform,
  ScrollView,
  StyleSheet,
  TouchableWithoutFeedback,
  View
} from "react-native";
import clone from "ramda/src/clone";
import styled, { withTheme } from "styled-components/native";
import { injectIntl } from "react-intl";
import { graphql } from "react-apollo";
import { Grid, Section, Block } from "react-native-responsive-layout";
import Collapsible from "react-native-collapsible";
import { SecuredComponent } from "cci-logic";
import { withRouter, hasPermissions } from "cci-utility";
import Icon from "../icon/FontAwesomePro";
import StoredImage from "../image/StoredImage";
import { Container } from "../layout/common";

/* Wrapper that contains the icons and text */
const ItemWrapper = styled(Container)`
  min-width: 250px;
`;

/* Icon that goes to the left of the menu item */
const LeftIcon = styled(Icon)`
  width: 20px;
  margin-right: 8px;
  text-align: center;
  color: ${({ theme }) => theme.mainMenuFontColor || theme.fontColor};
`;

/* Style for the text portion of regular text (not a title) */
const Text = styled.Text`
  flex: 1;

  font-size: ${({ theme }) => theme.mainMenuTextSize || 14}px;
  font-family: ${props => props.theme.fontFamily};
  color: ${({ theme }) => theme.mainMenuFontColor || theme.fontColor};
`;

/* Style for the text portion of the title */
const Title = styled(Text)`
  font-size: ${({ theme }) => theme.mainMenuTitleSize || 15}px;
  font-weight: bold;
`;

/* Icon that goes to the right of the menu item */
/* The 3px in margin-right are so that the icon is not cut off when we rotate it */
const RightIcon = styled(Icon)`
  margin-left: 8px;
  margin-right: 3px;
  color: ${({ theme }) => theme.mainMenuFontColor || theme.fontColor};

  /* Animates the rotation of the arrow */
  ${Platform.OS === "web" &&
    "transition-duration: 0.2s; transition-property: transform; transition-timing-function: linear;"};

  /* Rotates the icon so that the arrow points down */
  ${props => props.expanded && "transform: rotate(-90deg);"}
`;

/* Style for the container of the children of a menu item */
const ChildrenView = styled.View`
  padding-left: ${({ theme }) => theme.mainMenuItemLeftPadding || '20'}px;
  overflow: hidden;

  ${Platform.OS === "web" &&
    "transition-duration: 0.3s; transition-property: max-height;"};

  /* This transition solves the problem of not knowing the max-height: https://css-tricks.com/using-css-transitions-auto-dimensions/#comment-1607418 */
  ${props =>
    Platform.OS === "web" &&
    props.expanded === true &&
    "max-height: 55rem; transition-timing-function: cubic-bezier(1,0,1,0);"};

  /* Collapse (hide) the children of a menu item */
  /* This transition solves the problem of not knowing the max-height: https://css-tricks.com/using-css-transitions-auto-dimensions/#comment-1607418 */
  ${props =>
    Platform.OS === "web" &&
    props.expanded === false &&
    "max-height: 0; transition-timing-function: cubic-bezier(0,1,0,1);"};
`;

class VerticalMenu extends React.Component {
  constructor(props) {
    super(props);

    this.renderBranch = this.renderBranch.bind(this);
    this.onTap = this.onTap.bind(this);

    // We clone the items because the onTap function needs to change certain properties when a menu item is collapsed/expanded
    this.state = { items: clone(props.items) };
  }

  /**
   * Fired when one of the menu items is tapped.
   *
   * The action taken depends on the menu item type.
   */
  onTap(item) {
    const { props } = this;

    if (item.url) {
      //Link that takes the user to another page
      props.history.push(item.url);

      if (props.onNavigate) {
        props.onNavigate();
      }
    } else if (item.subMenu) {
      //Menu item that displays a sub-menu outside of our menu
      if (this.props.onShowSubMenu) {
        this.props.onShowSubMenu(item.subMenu);
      }
    } else if (item.collapsible) {
      //Menu item that collapses/expands its children
      item.expanded = !item.expanded;

      // Setting the state with the updated menu items triggers re-render
      this.setState({ items: this.state.items });
    }
  }

  renderBranch(menuItems, MenuContainer, options) {
    const { props } = this;

    return menuItems.map((item, index) => {
      const key = (item.title || item.text) + item.url;

      let itemMargins = item.title ? "20px 0 10px 0" : "10px 0 10px 0",
        menuItem,
        TextComponent = item.title ? Title : Text,
        rightIcon = item.rightIcon,
        itemChildren;

      if (item.children && item.collapsible) {
        /*
         * If the menu item has children and is collapsible then we override the right icon
         * to be the arrow.
         */
        rightIcon = "chevron-left";
      }

      //Content of the menu item, containing the left icon, text or title and the right icon
      const itemContent = (
        <React.Fragment>
          {item.leftIcon && (
            <LeftIcon
              name={item.leftIcon}
              iconSize={
                props.theme.mainMenuIconSize
                  ? props.theme.mainMenuIconSize
                  : item.title
                    ? props.theme.mainMenuTitleSize
                    : props.theme.mainMenuTextSize
              }
              weight={props.theme.mainMenuIconWeight}
            />
          )}

          <TextComponent>
            {props.intl.formatMessage({
              id: item.title || item.text,
              defaultMessage: item.defaultMessage
            })}
          </TextComponent>

          {!props.hideExpenderIcon && (
            <RightIcon
              name={rightIcon}
              expanded={item.expanded}
              iconSize={
                props.theme.mainMenuIconSize
                  ? props.theme.mainMenuIconSize
                  : item.title
                    ? props.theme.mainMenuTitleSize
                    : props.theme.mainMenuTextSize
              }
            />
          )}
        </React.Fragment>
      );

      //Wrap the content in a link or just have the simple text
      if (item.url || item.subMenu || item.collapsible) {
        //Content requires a wrapper that makes it tappable
        menuItem = (
          <TouchableWithoutFeedback onPress={() => this.onTap(item)}>
            <ItemWrapper
              flexDirection="row"
              alignItems="center"
              margins={itemMargins}
            >
              {itemContent}
            </ItemWrapper>
          </TouchableWithoutFeedback>
        );
      } else {
        //No wrapper necessary so we just render the content
        menuItem = (
          <ItemWrapper
            flexDirection="row"
            alignItems="center"
            margins={itemMargins}
          >
            {itemContent}
          </ItemWrapper>
        );
      }

      if (item.children) {
        if (Platform.OS === "web" || item.collapsible !== true) {
          //Either children for the web or native's children are not collapsible.
          //Web collapses via CSS vs native does so with a component
          itemChildren = this.renderBranch(item.children, ChildrenView, {
            expanded: item.expanded
          });
        } else {
          //HACK: This prevents the Collapsible component from changing his with to 100% when it has no children due to a Yoga or React Native bug.
          const style = item.expanded === false && { width: 0 };
          //For native we wrap the children in a component that collapses/expands with an animation.
          itemChildren = (
            <Collapsible collapsed={!item.expanded} style={style}>
              {this.renderBranch(item.children, ChildrenView, {
                expanded: item.expanded
              })}
            </Collapsible>
          );
        }
      }

      return (
        <SecuredComponent key={key} checkPermissions={item.checkPermissions}>
          <MenuContainer {...options}>
            {menuItem}

            {itemChildren}
          </MenuContainer>
        </SecuredComponent>
      );
    });
  }

  render() {
    const { props, state } = this;

    return (
      <Container {...props} flexDirection="column" alignItems="center">
        {/* Top menu options */}
        <Container
          flexDirection="column"
          alignItems="center"
          justifyContent="space-between"
          alignSelf="stretch"
          style={props.northStyle}
        >
          {props.north}
        </Container>

        {/* Center menu options */}
        <ScrollView contentContainerStyle={props.centerStyle}>
          {this.renderBranch(state.items, View, props.itemStyle)}
        </ScrollView>

        {/* Bottom menu options */}
        <Container
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          alignSelf="stretch"
          style={props.southStyle}
        >
          {props.south}
        </Container>
      </Container>
    );
  }
}

export default withRouter(withTheme(injectIntl(VerticalMenu)));
